import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Input, Paragraph, Table, ValidationMessage } from '@digdir/designsystemet-react';
import type { RiskMatrixSettings } from '../../../../api/hms';
import { bandInvalid, bandsInvalid, FIELD_IDS, likelihoodRanges } from '../riskMatrixView';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskMatrix.module.css';

type Props = {
  settings: RiskMatrixSettings;
  onChange: (next: Partial<RiskMatrixSettings>) => void;
};

export const LikelihoodCard = ({ settings, onChange }: Props): ReactElement => {
  const { t } = useTranslation();
  const bands = settings.likelihoodBands;
  const invalid = bandsInvalid(bands);

  const setBand = (index: number, value: string) => {
    const parsed = parseInt(value, 10);
    const next = [...bands];
    next[index] = Number.isNaN(parsed) ? 0 : parsed;
    onChange({ likelihoodBands: next });
  };

  return (
    <section aria-labelledby="hr-rm-likelihood" className={layout.card} data-gap="sm">
      <div className={layout.cardHeading}>
        <span className={layout.step}>{t('hrRiskMatrix.step', { step: 2, total: 4 })}</span>
        <Heading level={2} data-size="sm" id="hr-rm-likelihood">
          {t('hrRiskMatrix.likelihood.title')}
        </Heading>
        <Paragraph data-size="sm" className={layout.cardIntro}>
          {t('hrRiskMatrix.likelihood.intro')}
        </Paragraph>
      </div>
      <div className={layout.tableScroll}>
        <Table data-size="sm" className={classes.likelihoodTable}>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>{t('hrRiskMatrix.likelihood.level')}</Table.HeaderCell>
              <Table.HeaderCell>{t('hrRiskMatrix.likelihood.share')}</Table.HeaderCell>
              <Table.HeaderCell>{t('hrRiskMatrix.likelihood.upTo')}</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {likelihoodRanges(bands).map((row, index) => (
              <Table.Row key={row.level}>
                <Table.HeaderCell scope="row" className={layout.rowHeader}>
                  {t('hrRiskMatrix.likelihood.levelName', {
                    level: row.level,
                    name: t(`hms.likelihoodLevels.${row.level}`),
                  })}
                </Table.HeaderCell>
                <Table.Cell className={classes.numeric}>
                  {t('hrRiskMatrix.likelihood.range', { from: row.from, to: row.to })}
                </Table.Cell>
                <Table.Cell>
                  {index < 4 ? (
                    <span className={classes.percentInput}>
                      <Input
                        id={FIELD_IDS.band(index)}
                        data-size="sm"
                        type="number"
                        min={1}
                        max={99}
                        inputMode="numeric"
                        aria-label={t('hrRiskMatrix.likelihood.bandLabel', { level: row.level })}
                        aria-invalid={bandInvalid(bands, index) || undefined}
                        aria-describedby={invalid ? 'hr-rm-bands-error' : undefined}
                        value={bands[index]}
                        onChange={(event) => setBand(index, event.target.value)}
                      />
                      <span aria-hidden="true">%</span>
                    </span>
                  ) : (
                    <span className={layout.subtle}>{t('hrRiskMatrix.likelihood.hundred')}</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      {invalid && <ValidationMessage id="hr-rm-bands-error">{t('hrRiskMatrix.likelihood.error')}</ValidationMessage>}
    </section>
  );
};
